<script lang="ts">
	import { beforeUpdate } from 'svelte';
	import {
		get_mode,
		is_gen_2,
		isBluTRV,
		isWallDisplay,
		real_id,
		shouldExcludeBluTRVFromUpdate,
	} from '../../helpers/interface_helpers';

	import { updating_devices } from 'src/stores/device-runtime/process';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { for_update } from 'src/stores/device-runtime/firmware-ui';
	import { scenes } from 'src/stores/lists/scenes';
	// import { typeName } from 'src/stores/ui/breadcrumbs';
	import {
		deviceMainFilter,
		groupMainFilter,
		sceneMainFilter,
		roomMainFilter,
		devicesFilters,
		roomsGroupsFilter,
		roomsScenesFilter,
		alarmMainFilter,
		thermostatsMainFilter,
	} from 'src/stores/ui/filters';
	import { update } from 'src/stores/ui/edit_lists';
	import {
		sortByPosition,
		sortBy,
		filterScenesInitial,
		groupBy,
		filterDevice,
		filterGroup,
		filterScene,
		filterRoom,
		filterAlarm,
		filterThermostat,
	} from './list-helpers';
	import { defaultSelectedRoom, defaultSharedDevicesRoom } from 'src/helpers/constants';
	import { get } from 'svelte/store';
	import { devices_config } from '@devices/index';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import { isGatewayObserver, isGatewayObserverEnabled } from 'src/helpers/ble_devices_helpers';
	import { getDevicesThatCanApplySettings } from '../../helpers/settings/settings_config_helpers';
	import {
		matchesFirmwareVersion,
		getDeviceFirmwareString,
	} from '../../helpers/firmware_compare_helpers';
	import { canChangeView } from '../Sidepanel/view-helpers';

	export let type, type_id, room_list;

	$: allDeviceIds = Object.keys($devices);
	$: memberStatusStore = createMemberStatusSnapshot(allDeviceIds);
	$: memberSettingsStore = createMemberSettingsSnapshot(allDeviceIds);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	const getList = (list, filter, type) => {
		const isFilter = filter !== defaultSelectedRoom.value;
		let currentList = type === 'scenes' ? list.filter(filterScenesInitial) : list;

		if (isFilter && (type === 'thermostats' || type === 'scenes')) {
			currentList = currentList.filter((item) => {
				return item._meta.room == filter;
			});
		} else if (isFilter && type === 'groups') {
			currentList = currentList.filter((item) => {
				return item.room_id == filter;
			});
		}

		return currentList;
	};

	const groupByRoomCallBack = (group: []) => {
		return group.sort(sortByPosition);
	};

	$: lists = {
		devices: $devices,
		rooms: $rooms,
		room: $devices,
		groups: $groups,
		group: $devices,
		scenes: $scenes,
		alarms: $alarms,
		thermostats: $temp_schemes,
	};

	let list = [];

	beforeUpdate(() => {
		// Alex: TESTING this

		if (type === 'room') {
			// get list of all devices in particular rooms
			list = getList(Object.values(lists[room_list]), type_id, room_list);
		} else if (type === 'devices') {
			// get list of all devices
			list = getList(
				Object.values(lists[type]),
				$devicesFilters.rooms.find((filter) => filter.value).value,
				type,
			);
		} else if (type === 'scenes') {
			// get list of all scenes
			list = getList(
				Object.values(lists[type]),
				$roomsScenesFilter.rooms.find((filter) => filter.value).value,
				type,
			);
		} else if (type === 'groups' || type === 'thermostats') {
			// get list of all groups
			list = getList(
				Object.values(lists[type]),
				$roomsGroupsFilter.rooms.find((filter) => filter.value).value,
				type,
			);
		} else {
			// get list of all rooms & all alarms
			list = getList(Object.values(lists[type]), type_id, type);
		}

		switch (type) {
			case 'devices':
				const deviceSettings = memberSettings;

				// case 'device':
				// TO DO May be to make some sort for device position in its room
				list = Object.values(lists[type]);

				if ($deviceMainFilter) {
					const deviceMainFilterToLower = $deviceMainFilter.trim().toLowerCase();

					list = list.filter((device) => filterDevice(device, deviceMainFilterToLower));
				} else {
					list = list;
				}

				if ($devicesFilters.rooms.find((filter) => filter.value !== defaultSelectedRoom.value)) {
					list = list
						.filter((device) => {
							return (
								device.room_id ===
								$devicesFilters.rooms.find((filter) => filter.value).value
							);
						})
						.sort(sortByPosition);
				}

				if ($update.value) {
					// filter devices in update/change mode
					list = list.filter((device) => {
						const id = real_id(device.id);
						const hasUpdate = $for_update[id];
						const isUpdating = $updating_devices[id];
						const isOnline = memberStatus[real_id(device.id)]?.online;
						const isTypeUpdate = $update.type === 'update';

						if ($update.type === 'account_transfer_devices') {
							if (
								device.id.includes('_') ||
								isWallDisplay(device.type)
								//device.shared ||
								//device.shared_by_me - it should work by default, but we will test it for further info
							) {
								return; // hide wall displays and devices with '_' in id from account transfer witch are channels different from the first channel
							} else {
								return true;
							}
						}

						// Bulk "Move to another room" should keep every device selectable
						if ($update.type === 'bulk_move_devices_to_another_room') {
							return true;
						}

						if ($update.type === 'measure_peak_power' && device.category === 'light') {
							return; // hide light devices from peak power update
						}

						if (
							device.shared ||
							(['update', 'change'].includes($update.type) && device.gen == 'GBLE')
						) {
							/*
								Removing shared devices from firmware update and Wi-Fi lists,
								and global devices from Wi-Fi batch and batch update lists.
							*/
							return;
						}

						if (shouldExcludeBluTRVFromUpdate($update.type, device.type)) return; //hiding SBTR-001AEU from batch Firmware update and WiFi batch change because it is a BLE device, not an actual gen 3 device

						//hasCurrentAppSetting - checks if device meet specified filter setting (e.g. exclude_forgotten_lights, measure_peak_power, ai_device_failure) and returns true if it does
						let hasCurrentAppSetting = false;
						if (!['update', 'change'].includes($update.type)) {
							hasCurrentAppSetting = !!devices_config[device.type]?.modes[
								device.mode
							]?.settings
								.find((setting) => setting.title === 'application_settings')
								?.components.find((component) =>
									$update.type === 'measure_peak_power'
										? component.title === 'consumption_calculation'
										: component.title === $update.type,
								);

							if (
								$update.type === 'exclude_forgotten_lights' &&
								device.category === 'relay'
							) {
								hasCurrentAppSetting =
									hasCurrentAppSetting && device.appliance_type === 2;
							}
						}
						if ($update.type === 'offline_notifications') {
							//new batch offline notification
							const mode = get_mode(
								device,
								memberStatus[real_id(device.id)],
								memberSettings[real_id(device.id)],
							);
							hasCurrentAppSetting =
								(!device.shared &&
									devices_config[device.type]?.modes[mode]?.notifications?.some(
										(component) => {
											return component === 'offline';
										},
									)) ||
								false;
						}
						if ($update.type === 'ai_device_failure') {
							//new batch AI device failure
							//Todo: We have to check if device is 100% ai_model_readiness, but at this point of time we don't have this information
							let hasAICapability =
								devices_config[device.type]?.isPM &&
								/^(power_meter|relay|emeter)$/.test(device.category);

							if (hasAICapability) {
								// debugger;
								switch ($update.subType) {
									case 'enable_ai_model':
										hasCurrentAppSetting =
											hasAICapability &&
											(!device?.cloud_options?.hasOwnProperty(
												'ai_device_failure',
											) ||
												device.cloud_options.ai_device_failure === false);
										break;
									case 'disable_ai_model':
									case 'manage_ai_notifications':
										hasCurrentAppSetting =
											hasAICapability &&
											device?.cloud_options?.hasOwnProperty('ai_device_failure') &&
											device.cloud_options.ai_device_failure === true;
										break;
									default:
										hasCurrentAppSetting = false;
										break;
								}
							}
						}

						if ($update.type === 'apply_settings_configuration') {
							const allDevices = Object.values(get(devices));
							const compatibleDevices = getDevicesThatCanApplySettings(allDevices);
							const onlineCompatibleDevices = compatibleDevices.filter(
								(d) => memberStatus[real_id(d.id)]?.online,
							);
							hasCurrentAppSetting = onlineCompatibleDevices.some(
								(d) => d.id === device.id,
							);
						}

						if ($update.type === 'change_view') {
							// Only devices that have the per-device "Change view" entry
							// in the side panel are eligible for the bulk action.
							hasCurrentAppSetting = canChangeView(device);
						}

						return isTypeUpdate
							? (hasUpdate && isOnline) || isUpdating
							: $update.type === 'change'
								? isOnline
								: hasCurrentAppSetting;
					});
				}

				// START of devices filters

				// filter by shared/recently shared devices

				const selectedFilters = $devicesFilters.shared_devices.filter(
					(filter) => filter.selected,
				);

				if (selectedFilters.length > 0) {
					list = list.filter((device) => {
						return selectedFilters.some((filter) => {
							switch (filter.id) {
								case 'shared':
									return device.room_id === defaultSharedDevicesRoom.value;
								case 'all_shared':
									return device.shared;
								case 'all_owned':
									return !device.shared;
								case 'shared_by_me':
									return device.shared_by_me;
								default:
									return false;
							}
						});
					});
				}

				// filter devices by online/offline

				const isOnlineSelected = $devicesFilters.device_state.find(
					(filter) => filter.id === 'online',
				).selected;

				const isOfflineSelected = $devicesFilters.device_state.find(
					(filter) => filter.id === 'offline',
				).selected;

				if (isOnlineSelected && !isOfflineSelected) {
					// show only online devices
					list = list.filter((device) => memberStatus[real_id(device.id)]?.online);
				} else if (isOfflineSelected && !isOnlineSelected) {
					// show only offline devices
					list = list.filter((device) => !memberStatus[real_id(device.id)]?.online);
				} else {
					// show all devices
					list = list;
				}

				//filter devices by cloud enabled/cloud disabled

				const cloudEnabled = $devicesFilters.cloud_connectivity.find(
					(filter) => filter.id === 'cloudEnabled',
				)?.selected;
				const cloudDisabled = $devicesFilters.cloud_connectivity.find(
					(filter) => filter.id === 'cloudDisabled',
				)?.selected;

				if ((cloudEnabled && !cloudDisabled) || (cloudDisabled && !cloudEnabled)) {
					// Filter devices to show only cloud-enabled devices
					list = list.filter((device) => {
						const id = real_id(device.id);
						const settings = memberSettings[id];
						const cloudSettings = settings?.cloud;

						if (cloudSettings == null) return false;

						// Determine if the device is cloud-enabled based on generation
						const isCloudEnabled = is_gen_2(device)
							? cloudSettings?.enable === true
							: cloudSettings?.enabled === true;

						if (cloudDisabled && !cloudEnabled) {
							return !isCloudEnabled;
						}
						const cloudOnline = $devices[id]?.cloud_online;
						return isCloudEnabled && cloudOnline === true;
					});
				} else {
					// show all devices
					list = list;
				}

				// filter devices by additional options (rangeExtender, activeRangeExtender, bluetoothDevices, bluetoothGateways, activeBluetoothGateways, activatedAIModel, virtualDevices, peripheralDevices, activeAccessPoint)
				const filterMapping = {
					rangeExtender: (device) =>
						device.channel < 100 &&
						deviceSettings[real_id(device.id)]?.wifi?.ap?.range_extender,
					activeRangeExtender: (device) =>
						device.channel < 100 &&
						deviceSettings[real_id(device.id)]?.wifi?.ap?.range_extender?.enable,
					bluetoothDevices: (device) => device.gen === 'GBLE',
					bluetoothGateways: (device) => device.channel < 100 && isGatewayObserver(device),
					activeBluetoothGateways: (device) =>
						device.channel < 100 && isGatewayObserverEnabled(device),
					activatedAIModel: (device) => {
						return (
							devices_config[device.type]?.isPM &&
							/^(power_meter|relay|emeter)$/.test(device.category) &&
							$devices[device.id]?.cloud_options?.hasOwnProperty('ai_device_failure')
						);
					},
					virtualDevices: (device) => {
						return device.category === 'virtual_device';
					},
					peripheralDevices: (device) => {
						return isPeripheralDevice(device.type);
					},
					activeAccessPoint: (device) =>
						device.channel < 100 &&
						Number(device.gen) >= 2 &&
						deviceSettings[real_id(device.id)]?.wifi?.ap?.enable,
				};

				const selectedMoreFilters = $devicesFilters.more_filters.filter(
					(filter) => filter.selected && filterMapping[filter.id],
				);

				if (selectedMoreFilters.length > 0) {
					list = list.filter((device) => {
						return selectedMoreFilters.some((filter) => filterMapping[filter.id](device));
					});
				}

				// filter devices by appliance types
				// (2-lighting,3-entertainment,4-heating/cooling, 5-refrigeration, 6-laundry & cleaning,
				// 7-cooking, 8-pool and garden, 9-electric vehicle, 10-ventilation , 11-grid source,
				// 12-generated energy, 13-water heater, 14-roller , 15-other, 16-garage door)

				if ($devicesFilters.appliance_types.find((filter) => filter.selected)?.selected) {
					// get the selected filters by id
					const storeFilters = $devicesFilters.appliance_types
						.filter((filter) => filter.selected)
						.map((f) => f.id);

					// return devices with appliance type === id
					list = list.filter((device) => {
						if (device.category === 'roller') {
							// if device is roller, then show it as "roller"
							// and don't check for appliance_type or relay_usage
							return storeFilters.includes(15) && device.category === 'roller';
						} else if (
							device.category === 'relay' &&
							!device.hasOwnProperty('appliance_type')
						) {
							// if device is relay and doesn't have appliance type, then show it as "other"
							return storeFilters.includes(14) && device.category === 'relay';
						} else if (device.appliance_type === 'garage_door') {
							return storeFilters.includes(16);
						} else {
							return (
								// storeFilters.includes(device.appliance_type) covers all other cases
								(storeFilters.includes(2) && device.category === 'light') ||
								storeFilters.includes(device.appliance_type)
							);
						}
					});
				}

				// filter devices by firmware version
				if ($devicesFilters.firmware_version) {
					list = list.filter((device) => {
						const fwString = getDeviceFirmwareString(device, memberSettings, memberStatus);
						return matchesFirmwareVersion(fwString, $devicesFilters.firmware_version);
					});
				}

				// END of devices filters

				list = list.sort((x, y) => {
					return sortBy(x, y, 'room_id');
				});

				// typeName.set('');
				break;
			case 'room':
				if (room_list === 'thermostats' || room_list === 'scenes') {
					list = list
						.filter((i) => {
							return i._meta.room == type_id;
						})
						.sort((x, y) => sortBy(x._meta, y._meta, 'position'))
						.map((e: any) => {
							if (e.id === undefined) {
								e.id = e._id;
							}
							return e;
						});
				} else {
					list = list
						.filter((i) => {
							const roomFilter =
								type_id == -1 || i.room_id == type_id || i.room == type_id;
							if ($update.value && $update.type === 'account_transfer_devices') {
								// filter devices for account transfer
								// hide wall displays and devices with '_' in id
								const isValidDevice = !i.id.includes('_') && !isWallDisplay(i.type);
								return roomFilter && isValidDevice;
							}

							// Keep batch "Change view" eligibility consistent between the
							// "All devices" tab and the room view of the new layout — only
							// devices that expose the per-device "Change view" entry should
							// appear as selectable when this bulk command is active.
							if ($update.value && $update.type === 'change_view') {
								return roomFilter && canChangeView(i);
							}

							return roomFilter;
						})
						.sort(sortByPosition);
				}

				// typeName.set($rooms[type_id]?.name);
				break;
			case 'group':
				// case 'device':
				// list = list.filter(i => {
				list = $groups[type_id].devices
					.map((id) => {
						if ($devices[id]) {
							return $devices[id];
						}
						if (typeof $devices[real_id(id)] === 'undefined') {
							return { id: id, deleted: true };
						}
					})
					.filter((e) => e !== undefined);
				// });

				// typeName.set($groups[type_id].name);
				break;
			case 'rooms':
				// Apply search filter if present
				if ($roomMainFilter) {
					const roomMainFilterToLower = $roomMainFilter.trim().toLowerCase();

					list = list.filter((room) => filterRoom(room, roomMainFilterToLower));
				}

				list = list.sort(sortByPosition);
				break;
			case 'groups':
				// Apply search filter if present
				if ($groupMainFilter) {
					const groupMainFilterToLower = $groupMainFilter.trim().toLowerCase();

					list = list.filter((group) => filterGroup(group, groupMainFilterToLower));
				}

				// Sort groups in room by position and then sort by room id
				list = [...groupBy(list, 'room_id', groupByRoomCallBack)].sort((x, y) =>
					sortBy(x, y, 'room_id'),
				);
				break;
			case 'alarms':
				list = list
					.map((e: any, ind) => {
						if (typeof e?._meta?.position === 'undefined') {
							e._meta.position = ind++;
						}
						if (typeof e?._meta?.room === 'undefined') {
							e._meta.room = -1;
						}
						e.id = e._id;
						return e;
					})
					.sort((x, y) => sortBy(x._meta, y._meta, 'position'));

				if ($alarmMainFilter) {
					const alarmMainFilterToLower = $alarmMainFilter.trim().toLowerCase();

					list = list.filter((alarm) => filterAlarm(alarm, alarmMainFilterToLower));
				}
				break;
			case 'thermostats':
				// Apply search filter if present
				if ($thermostatsMainFilter) {
					const thermostatsMainFilterToLower = $thermostatsMainFilter.trim().toLowerCase();

					list = list.filter((thermostat) =>
						filterThermostat(thermostat, thermostatsMainFilterToLower),
					);
				}

				list = list
					.map((e: any) => {
						if (e.id === undefined) {
							e.id = e._id;
						}
						return e;
					})
					.sort((x, y) => sortBy(x._meta, y._meta, 'position'));
				break;

			case 'scenes':
				list = list.map((e: any) => {
					if (e.id === undefined) {
						e.id = e._id;
					}
					if (typeof e?._meta?.room === 'undefined') {
						e._meta.room = -1;
					}
					// delete e.position;
					// delete e.image;
					// delete e.name;

					return e;
				});

				// Apply search filter if present
				if ($sceneMainFilter) {
					const sceneMainFilterToLower = $sceneMainFilter.trim().toLowerCase();

					list = list.filter((scene) => filterScene(scene, sceneMainFilterToLower));
				}

				list = list.sort((x, y) => sortBy(x._meta, y._meta, 'position'));
				break;

			default:
				break;
		}
	});
</script>

<slot {list} />
